import { useEffect, useState } from "react";
import { Table, Badge, Checkbox } from "rsuite";
import { PanelService } from "../../../../network/panel/network";
import { IDomain, DomainStatus } from "@bprotsyk/aso-core";
import React from "react";
import InfoRoundIcon from "@rsuite/icons/InfoRound";
import TimeIcon from "@rsuite/icons/Time";
import RemindRoundIcon from "@rsuite/icons/RemindRound";
import ReloadIcon from "@rsuite/icons/Reload";
import ArrowUpIcon from '@rsuite/icons/ArrowUp';
import ArrowDownIcon from '@rsuite/icons/ArrowDown';
import "./_table.scss";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

const { Column, HeaderCell, Cell } = Table;

type DomainSortKeys = keyof IDomain | "";
type SortType = "asc" | "desc" | undefined;

interface FilterState {
  status: string[];
  archived: string[];
  target: string[];
}

interface IHandleEditingDomain {
  onHandleEditingDomain: (e: any) => void;
  searchQuery: string;
  filterSelectors: FilterState;
  onToggleButtonShow: (e: boolean) => void;
  tableData: IDomain[];
  onRefresh?: (domain: IDomain) => void;
  tableHeight: number;
  onStatusChange?: (domainId: string, newStatus: DomainStatus) => void;
  openDropdownId?: string | null;
  dropdownPosition?: { x: number; y: number } | null;
  onStatusCellClick?: (domainId: string, event: React.MouseEvent) => void;
  dropdownRefs?: React.MutableRefObject<Map<string, HTMLDivElement>>;
  onRenewDomain?: (domain: IDomain) => void;
}

// Helper functions for status
const getStatusIndicator = (status: DomainStatus) => {
  switch (status) {
    case DomainStatus.PENDING:
      return "#ffeb3b";
    case DomainStatus.READY:
    case DomainStatus.READY_AND_NOTIFIED:
      return "#4caf50";
    default:
      return "#9e9e9e";
  }
};

const getStatusText = (status: DomainStatus): string => {
  switch (status) {
    case DomainStatus.PENDING:
      return "В очікуванні";
    case DomainStatus.READY:
      return "Готовий";
    case DomainStatus.READY_AND_NOTIFIED:
      return "Готовий і сповіщений";
    default:
      return "Невідомо";
  }
};

const getAvailableStatusTransitions = (currentStatus: DomainStatus): DomainStatus[] => {
  switch (currentStatus) {
    case DomainStatus.PENDING:
      return [DomainStatus.READY, DomainStatus.READY_AND_NOTIFIED];
    case DomainStatus.READY:
      return [DomainStatus.PENDING, DomainStatus.READY_AND_NOTIFIED];
    case DomainStatus.READY_AND_NOTIFIED:
      return [DomainStatus.PENDING, DomainStatus.READY];
    default:
      return [DomainStatus.PENDING, DomainStatus.READY, DomainStatus.READY_AND_NOTIFIED];
  }
};

const ContainerTable = React.memo(({
  onHandleEditingDomain,
  searchQuery,
  filterSelectors,
  onToggleButtonShow,
  tableData,
  onRefresh,
  tableHeight,
  onStatusChange,
  openDropdownId,
  dropdownPosition,
  onStatusCellClick,
  dropdownRefs,
  onRenewDomain
}: IHandleEditingDomain) => {
  const [sortColumn, setSortColumn] = useState<DomainSortKeys>("");
  const [data, setData] = useState<IDomain[]>([]);
  const [sortType, setSortType] = useState<SortType>(undefined);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [tableSize, setTableSize] = useState(window.innerWidth - 20);
  const [hidenColum, setHidenColum] = useState(tableSize < 600);

  useEffect(() => {
    setData(tableData);
  }, [tableData]);

  const handleSort = (dataKey: string, sortType: 'asc' | 'desc' | undefined) => {
    setLoading(true);
    setSortColumn(dataKey as DomainSortKeys);
    setSortType(sortType);
    setTimeout(() => setLoading(false), 200);
  };

  const getData = () => {
    let filteredData = [...data];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(domain =>
        domain.name?.toLowerCase().includes(query) ||
        domain.ip?.toLowerCase().includes(query)
      );
    }

    // First, sort by active status (active domains first), then by expiration urgency
    filteredData.sort((a, b) => {
      const aIsArchived = a.archived === true;
      const bIsArchived = b.archived === true;

      // Archived domains go to bottom
      if (aIsArchived && !bIsArchived) return 1;
      if (!aIsArchived && bIsArchived) return -1;

      // For active domains, sort by expiration urgency
      if (!aIsArchived && !bIsArchived) {
        const aDaysUntilExpiration = Math.floor((a.expiresAt - Date.now()) / (24 * 60 * 60 * 1000));
        const bDaysUntilExpiration = Math.floor((b.expiresAt - Date.now()) / (24 * 60 * 60 * 1000));

        // Domains expiring sooner come first
        if (aDaysUntilExpiration <= 14 && aDaysUntilExpiration >= 0 && (bDaysUntilExpiration > 14 || bDaysUntilExpiration < 0)) return -1;
        if (bDaysUntilExpiration <= 14 && bDaysUntilExpiration >= 0 && (aDaysUntilExpiration > 14 || aDaysUntilExpiration < 0)) return 1;

        // Within expiring domains, sort by days left (soonest first)
        if (aDaysUntilExpiration <= 14 && aDaysUntilExpiration >= 0 && bDaysUntilExpiration <= 14 && bDaysUntilExpiration >= 0) {
          return aDaysUntilExpiration - bDaysUntilExpiration;
        }

        // Expired domains after expiring but before normal domains
        if (aDaysUntilExpiration < 0 && bDaysUntilExpiration >= 0) return -1;
        if (bDaysUntilExpiration < 0 && aDaysUntilExpiration >= 0) return 1;
      }

      // If sortColumn and sortType are specified, apply additional sorting
      if (sortColumn && sortType) {
        const aValue = a[sortColumn as keyof IDomain];
        const bValue = b[sortColumn as keyof IDomain];

        if (sortColumn === 'expiresAt') {
          const aDate = new Date(aValue as number).getTime();
          const bDate = new Date(bValue as number).getTime();
          return sortType === 'asc' ? aDate - bDate : bDate - aDate;
        }

        if (aValue < bValue) return sortType === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortType === 'asc' ? 1 : -1;
      }

      return 0;
    });

    return filteredData;
  };

  const timestampToDaysWithHandlingOneDay = (milliseconds: number, isArchived: boolean = false) => {
    const days = Math.round(milliseconds / (1000 * 60 * 60 * 24));

    // For archived domains, always return white/grey text
    if (isArchived) {
      if (window.innerWidth <= 480) {
        if (days % 10 > 1 && days % 10 < 5 && !(days % 100 >= 11 && days % 100 <= 14)) return { text: days, color: '#9e9e9e' };
        return { text: <RemindRoundIcon className="expired_icon" />, color: '#9e9e9e' };
      }
      if (days === 0) return { text: "Сьогодні", color: '#9e9e9e' };
      if (days % 10 === 1 && !(days % 100 >= 11 && days % 100 <= 14)) return { text: `${days} день`, color: '#9e9e9e' };
      if (days % 10 > 1 && days % 10 < 5 && !(days % 100 >= 11 && days % 100 <= 14)) return { text: `${days} дні`, color: '#9e9e9e' };
      if (days < 0) return { text: days, color: '#9e9e9e' };
      return { text: `${days} днів`, color: '#9e9e9e' };
    }

    // For active domains, use color coding
    let color = '#4caf50'; // green for >14 days
    if (days <= 14 && days > 0) {
      color = '#ff9800'; // yellow for <=14 days
    } else if (days <= 0) {
      color = '#f44336'; // red for expired
    }

    if (window.innerWidth <= 480) {
      if (days % 10 > 1 && days % 10 < 5 && !(days % 100 >= 11 && days % 100 <= 14)) return { text: days, color };
      return { text: days, color };
    }
    if (days === 0) return { text: "Сьогодні", color };
    if (days % 10 === 1 && !(days % 100 >= 11 && days % 100 <= 14)) return { text: `${days} день`, color };
    if (days % 10 > 1 && days % 10 < 5 && !(days % 100 >= 11 && days % 100 <= 14)) return { text: `${days} дні`, color };
    if (days < 0) return { text: `${days} днів`, color };
    return { text: `${days} днів`, color };
  };

  useEffect(() => {
    const handleResize = () => {
      setTableSize(window.innerWidth - 20);
      setHidenColum(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  return (
    <div className="domains-table">
      <Table
        data={getData()}
        loading={loading}
        bordered
        cellBordered
        locale={{ loading: 'Завантаження...' }}
        virtualized
        height={tableHeight}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSort}
      >
        <Column width={60} align="center">
          <HeaderCell>
            <Checkbox
              className="custom_checkbox"
              checked={checked}
              onChange={() => setChecked(!checked)}
            />
          </HeaderCell>
          <Cell>
            {(rowData: IDomain) => (
              <Checkbox
                className="custom_checkbox"
                checked={checked}
                onChange={() => setChecked(!checked)}
              />
            )}
          </Cell>
        </Column>

        {!hidenColum && (
          <Column width={80} align="center" sortable>
            <HeaderCell>
              <Badge style={{ background: "#34c3ff" }} />
            </HeaderCell>
            <Cell dataKey="status">
              {(rowData: IDomain) => (
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusCellClick?.(rowData.id, e);
                  }}
                  className="domains-status-cell noselect"
                  style={{ 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                  }}
                >
                  <Badge
                    style={{
                      background: getStatusIndicator(rowData.status)
                    }}
                  />
                </div>
              )}
            </Cell>
          </Column>
        )}

        <Column flexGrow={2} sortable align="left">
          <HeaderCell>Хост</HeaderCell>
          <Cell dataKey="name">
            {(rowData: IDomain) => (
              <div className="host-cell noselect" onClick={(e) => e.stopPropagation()}>
                <span 
                  className="text" 
                  title={rowData.name}
                  onClick={() => onHandleEditingDomain(rowData)}
                  style={{ cursor: 'pointer' }}
                >
                  {rowData.name}
                </span>
                <FontAwesomeIcon 
                  icon={faCopy}
                  className="copy-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(rowData.name || '');
                  }}
                />
              </div>
            )}
          </Cell>
        </Column>

        <Column width={120} align="center" sortable>
          <HeaderCell>
            <TimeIcon className="time_icon" />
          </HeaderCell>
          <Cell dataKey="expiresAt">
            {(rowData: IDomain) => {
              const daysUntilExpiration = Math.floor((rowData.expiresAt - Date.now()) / (24 * 60 * 60 * 1000));
              const canRenew = daysUntilExpiration <= 14; // Allow renewal for expired domains too
              const isArchived = rowData.archived === true;
              const expirationData = timestampToDaysWithHandlingOneDay(rowData.expiresAt - Date.now(), isArchived);


              return (
                <div
                  className={`date-cell ${canRenew ? 'renewable' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canRenew && onRenewDomain) {
                      const currentExpiration = new Date(rowData.expiresAt);
                      const newExpiration = new Date(Math.max(rowData.expiresAt, Date.now()) + (365 * 24 * 60 * 60 * 1000));
                      const confirmRenew = window.confirm(
                        `Ви дійсно хочете поновити домен "${rowData.name}" на 1 рік?\n\n` +
                        `Поточна дата закінчення: ${currentExpiration.toLocaleDateString('uk-UA')}\n` +
                        `Нова дата закінчення: ${newExpiration.toLocaleDateString('uk-UA')}`
                      );
                      if (confirmRenew) {
                        onRenewDomain(rowData);
                      }
                    }
                  }}
                  style={{
                    cursor: canRenew ? 'pointer' : 'default',
                    textDecoration: canRenew ? 'underline' : 'none',
                    color: expirationData.color
                  }}
                >
                  {expirationData.text}
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={1} align="left" sortable>
          <HeaderCell>A @</HeaderCell>
          <Cell dataKey="ip">
            {(rowData: IDomain) => (
              <div className="ip-cell noselect" onClick={(e) => e.stopPropagation()}>
                <span
                  className="ip-text"
                  onClick={() => onHandleEditingDomain(rowData)}
                  title={rowData.ip}
                  style={{ cursor: 'pointer' }}
                >
                  {rowData.ip}
                </span>
                <FontAwesomeIcon 
                  icon={faCopy}
                  className="copy-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(rowData.ip || '');
                  }}
                />
              </div>
            )}
          </Cell>
        </Column>

        <Column flexGrow={1} align="center">
          <HeaderCell>Примітки</HeaderCell>
          <Cell>
            {(rowData: IDomain) => (
              <InfoRoundIcon 
                className="info_icon"
                onClick={() => onHandleEditingDomain(rowData)}
              />
            )}
          </Cell>
        </Column>

        <Column flexGrow={1} align="center">
          <HeaderCell>Автоновлення</HeaderCell>
          <Cell>
            {(rowData: IDomain) => (
              <ReloadIcon 
                className="reload_icon"
                onClick={() => onRefresh?.(rowData)}
              />
            )}
          </Cell>
        </Column>

      
      </Table>

      {/* Domain Status Dropdown - ізольований від app стилів */}
      {openDropdownId !== null && dropdownPosition && (() => {
        const domain = tableData.find(d => d.id === openDropdownId);
        if (!domain) return null;
        
        const currentStatus = domain.status;
        const availableTransitions = getAvailableStatusTransitions(currentStatus);
        
        return (
          <div
            ref={(el) => {
              if (el && openDropdownId) dropdownRefs?.current.set(openDropdownId, el);
            }}
            className="domains-status-dropdown"
            style={{
              position: 'absolute',
              left: dropdownPosition.x,
              top: dropdownPosition.y - 12,
              zIndex: 10000,
              backgroundColor: '#2c3e50',
              border: '1px solid #34495e',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              minWidth: '180px'
            }}
          >
            {availableTransitions.map((status) => (
              <div
                key={status}
                onClick={() => onStatusChange?.(domain.id, status)}
                className="domains-status-dropdown-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: currentStatus === status ? '#34495e' : 'transparent',
                  transition: 'background-color 0.2s ease',
                  fontSize: '12px',
                  color: '#ecf0f1'
                }}
                onMouseEnter={(e) => {
                  if (currentStatus !== status) {
                    e.currentTarget.style.backgroundColor = '#34495e';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentStatus !== status) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div 
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: getStatusIndicator(status),
                    flexShrink: 0
                  }}
                />
                <span>{getStatusText(status)}</span>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
});

export default ContainerTable;
